import type { BookingLifecycleStatus } from "@/types";

export type TimelineStepTone = "progress" | "failure" | "upcoming";

export interface AdminBookingTimelineStep {
  status: BookingLifecycleStatus;
  tone: TimelineStepTone;
  isCurrent: boolean;
}

export interface AdminBookingTimeline {
  steps: AdminBookingTimelineStep[];
  isFailurePath: boolean;
}

const FAILURE_STATUSES: ReadonlySet<BookingLifecycleStatus> = new Set([
  "CANCELED",
  "REJECTED",
  "ABANDONED",
]);

function happyPath(
  requiresHandshake: boolean,
): BookingLifecycleStatus[] {
  return requiresHandshake
    ? ["CREATED", "PENDING_HANDSHAKE", "ACCEPTED", "COMPLETED"]
    : ["CREATED", "ACCEPTED", "COMPLETED"];
}

function failurePath(
  status: BookingLifecycleStatus,
  requiresHandshake: boolean,
  reachedAccepted: boolean,
): BookingLifecycleStatus[] {
  if (status === "ABANDONED") {
    return ["CREATED", "ABANDONED"];
  }
  if (status === "REJECTED") {
    return requiresHandshake
      ? ["CREATED", "PENDING_HANDSHAKE", "REJECTED", "COMPLETED"]
      : ["CREATED", "REJECTED", "COMPLETED"];
  }
  // CANCELED
  if (requiresHandshake) {
    return reachedAccepted
      ? ["CREATED", "PENDING_HANDSHAKE", "ACCEPTED", "CANCELED", "COMPLETED"]
      : ["CREATED", "PENDING_HANDSHAKE", "CANCELED", "COMPLETED"];
  }
  return reachedAccepted
    ? ["CREATED", "ACCEPTED", "CANCELED", "COMPLETED"]
    : ["CREATED", "CANCELED", "COMPLETED"];
}

/**
 * Derive a visual booking lifecycle timeline for the admin detail page.
 * Happy path stays green; canceled/rejected/abandoned use a red progress rail.
 *
 * @param reachedAccepted - When status is CANCELED, whether the booking had
 *   reached ACCEPTED before cancel (so the failure path includes ACCEPTED).
 */
export function buildAdminBookingTimeline(
  status: BookingLifecycleStatus,
  requiresHandshake: boolean,
  reachedAccepted = false,
): AdminBookingTimeline {
  const isFailurePath = FAILURE_STATUSES.has(status);
  const keys = isFailurePath
    ? failurePath(status, requiresHandshake, reachedAccepted)
    : happyPath(requiresHandshake);
  const current = Math.max(0, keys.indexOf(status));

  const steps: AdminBookingTimelineStep[] = keys.map((key, index) => {
    const isCurrent = index === current;
    let tone: TimelineStepTone =
      index <= current
        ? isFailurePath
          ? "failure"
          : "progress"
        : "upcoming";
    if (isFailurePath && key === "COMPLETED" && status !== "COMPLETED") {
      tone = "upcoming";
    }
    return { status: key, tone, isCurrent };
  });

  return { steps, isFailurePath };
}
